import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import App from './App'
import { AuthGuard } from './components/AuthGuard'
import { DashboardLayout } from './components/layouts/DashboardLayout'
import { ConsentRefreshGate2026 } from './features/consentRefresh2026/ConsentRefreshGate2026'
import './i18n/config'
import './index.css'
import { AddBookingPage } from './pages/AddBookingPage'
import { AddChildPage } from './pages/AddChildPage'
import { AddMedicalHistory } from './pages/AddMedicalHistory'
import { BookingsPage } from './pages/BookingsPage'
import { CheckoutPage } from './pages/CheckoutPage'
import { ChildrenPage } from './pages/ChildrenPage'
import { CookiePolicyPage } from './pages/CookiePolicyPage'
import { DashboardPage } from './pages/DashboardPage'
import { EditBookingPage } from './pages/EditBookingPage'
import { EditChildPage } from './pages/EditChildPage'
import { EditProfilePage } from './pages/EditProfilePage'
import { LoginPage } from './pages/LoginPage'
import { MedicalHistoryListPage } from './pages/MedicalHistoryListPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { PaymentFailurePage } from './pages/PaymentFailurePage'
// PaymentHistoryPage merged into PaymentsPage - route redirects to /payments
import { PaymentRedirectPage } from './pages/PaymentRedirectPage'
import { PaymentSuccessPage } from './pages/PaymentSuccessPage'
import { PaymentsPage } from './pages/PaymentsPage'
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage'
import { ProfileCompletionPage } from './pages/ProfileCompletionPage'
import { RegisterPage } from './pages/RegisterPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { ReturnAndCancelPolicy } from './pages/ReturnAndCancelPolicy'
import { SetNewPasswordPage } from './pages/SetNewPasswordPage'
import { TermsOfServicePage } from './pages/TermsOfServicePage'
import { VerificationPendingPage } from './pages/VerificationPendingPage'
import { VerifyEmailPage } from './pages/VerifyEmailPage'
import { initTracking } from './utils/tracking'

// Initialize tracking (Facebook Pixel, Google Ads - production only)
initTracking()

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <NotFoundPage />,
    children: [
      // Auth pages (no dashboard layout)
      {
        path: 'login',
        element: <LoginPage />
      },
      {
        path: 'register',
        element: <RegisterPage />
      },
      {
        path: 'verification-pending',
        element: <VerificationPendingPage />
      },
      {
        path: 'verify-email/:token',
        element: <VerifyEmailPage />
      },
      {
        path: 'reset-password',
        element: <ResetPasswordPage />
      },
      {
        path: 'reset-password/:token',
        element: <SetNewPasswordPage />
      },
      {
        path: 'profile-completion',
        element: (
          <AuthGuard requireEmailVerificationOnly>
            <ProfileCompletionPage />
          </AuthGuard>
        )
      },
      // Legal pages (no auth required)
      {
        path: 'terms-of-service',
        element: <TermsOfServicePage />
      },
      {
        path: 'privacy-policy',
        element: <PrivacyPolicyPage />
      },
      {
        path: 'return-and-cancel-policy',
        element: <ReturnAndCancelPolicy />
      },
      {
        path: 'cookie-policy',
        element: <CookiePolicyPage />
      },
      // Piraeus ePay callbacks (no auth)
      {
        path: 'payments/epay/success',
        element: <PaymentSuccessPage />
      },
      {
        path: 'payments/epay/failure',
        element: <PaymentFailurePage />
      },
      // Authenticated dashboard routes
      {
        element: (
          <AuthGuard>
            <ConsentRefreshGate2026>
              <DashboardLayout />
            </ConsentRefreshGate2026>
          </AuthGuard>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard" replace />
          },
          {
            path: 'dashboard',
            element: <DashboardPage />
          },
          {
            path: 'profile/edit',
            element: <EditProfilePage />
          },
          // Children
          {
            path: 'children',
            element: <ChildrenPage />
          },
          {
            path: 'children/add',
            element: <AddChildPage />
          },
          {
            path: 'children/:id',
            element: <EditChildPage />
          },
          // Bookings
          {
            path: 'bookings',
            element: <BookingsPage />
          },
          {
            path: 'bookings/add',
            element: <AddBookingPage />
          },
          {
            path: 'bookings/:id',
            element: <EditBookingPage />
          },
          // Medical History
          {
            path: 'medical-history',
            element: <MedicalHistoryListPage />
          },
          {
            path: 'medical-history/add',
            element: <AddMedicalHistory />
          },
          // Payments
          {
            path: 'payments',
            element: <PaymentsPage />
          },
          {
            path: 'payments/checkout',
            element: <CheckoutPage />
          },
          {
            path: 'payments/redirect',
            element: <PaymentRedirectPage />
          },
          {
            path: 'payments/history',
            element: <Navigate to="/payments" replace />
          },
        ]
      },
    ]
  }
])

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
)
